/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useSignals} from '@preact/signals-react/runtime';
import {
  MARK_AS_READ_RESPONSE,
  setNotificationsPanelStatus
} from '@ion/applications/src/notifications/state';
import {markPanelSeen} from '@/routes/provisioning/jobsearchengine/JobNotifications';
import {
  liveJobsEnabled
} from '@/routes/provisioning/jobsearchengine/signals/liveUpdates';
import {toJobNotifications} from '@/routes/provisioning/jobsearchengine/toJobNotification';
import {getNotifications} from '@/routes/provisioning/jobsearchengine/WatchedJobs';
import {
  notificationPanelOpenSelector,
  unreadNotificationsCountSelector
} from '@/store/selectors/notifications';

export interface JobNotificationsPanelResult {
  show: boolean,
  unreadCount: number,
  toggle: () => void
}

/**
 * The header bell's state and its click, in one place, so the desktop and
 * mobile headers cannot drift on what opening the panel does.
 *
 *  The panel status action is the **shared** one from
 * `@ion/applications/src/notifications/state`. ion-suite has its own action of
 * the same name over its own reducer, and ADM has the shared reducer wired - so
 * a copy-paste from ion-suite's header dispatches the wrong one and the panel
 * silently never opens.
 *
 * Opening the panel marks everything seen, in both places it is recorded: the
 * badge count in the reducer, and `hasSeen` in preferences so it survives a
 * reload.
 *
 * `show` is the feature flag: the bell is new to ADM's header on this branch,
 * so it is not offered at all while the flag is off. It is returned from here
 * rather than read in each header for the same reason `toggle` is - two headers
 * reading the flag themselves is two chances to drift.
 *
 * ```typescript
 *
 *   import useJobNotificationsPanel from './useJobNotificationsPanel';
 *
 *   const {show, unreadCount, toggle} = useJobNotificationsPanel();
 *
 * ```
 *
 * @returns Whether to offer the bell, the badge count, and its click handler.
 */
export const useJobNotificationsPanel = (): JobNotificationsPanelResult => {
  useSignals();

  const dispatch    = useDispatch();
  const open        = useSelector(notificationPanelOpenSelector);
  const unreadCount = useSelector(unreadNotificationsCountSelector);

  const toggle = useCallback(function() {
    const opening = !open;

    dispatch(setNotificationsPanelStatus(opening));
    if (opening) {
      dispatch({
        type          : MARK_AS_READ_RESPONSE,
        notifications : toJobNotifications(getNotifications())
      });
      markPanelSeen();
    }
  }, [open]);

  return {show: liveJobsEnabled.value, unreadCount, toggle};
};

export default useJobNotificationsPanel;
